import { useTranslation } from "react-i18next";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "~/atoms/button";
import { Input } from "~/atoms/input";
import { AuthLayout } from "../../layouts/Auth";
import {
  forgotPassword,
  getRedirectUrlActivate,
  resetPassword,
} from "../../api/v2/requests";
import { OverlaySpinner } from "~/atoms/spinner";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/atoms/form";
import i18n from "~/i18n";
import { useCheckResetToken } from "~/api/v2/useCheckResetToken";
import NotFound from "~/pages/NotFound";

const FormSchema = z
  .object({
    password: z
      .string()
      .min(
        8,
        i18n.t(($) => $.settings.password.tooShort),
      )
      .regex(
        /[A-Z]/,
        i18n.t(($) => $.settings.password.needsUppercase),
      )
      .regex(
        /[a-z]/,
        i18n.t(($) => $.settings.password.needsLowercase),
      )
      .regex(
        /[0-9]/,
        i18n.t(($) => $.settings.password.needsNumber),
      ),
    repeatPass: z.string(),
  })
  .refine((data) => data.password === data.repeatPass, {
    message: i18n.t(($) => $.settings.password.noMatch),
    path: ["repeatPass"],
  });

type PasswordFormValues = z.infer<typeof FormSchema>;

export const ResetPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token") ?? params.token;

  const { data, isLoading, error } = useCheckResetToken(token);

  const form = useForm({
    resolver: zodResolver(FormSchema),
    mode: "onChange",
    defaultValues: {
      password: "",
      repeatPass: "",
    },
  });

  const onSubmit = async (values: PasswordFormValues) => {
    try {
      if (!token) return;
      await resetPassword(token, values.password);
      await navigate("/reset-password-complete");
    } catch {
      toast.error(t(($) => $.toasts.error.passNotReset));
    }
  };

  const handleSignInWithSso = async (token: string) => {
    try {
      const res = await getRedirectUrlActivate(token);
      window.location.href = res.redirectUrl;
    } catch {
      toast.error(t(($) => $.toasts.error.generic));
    }
  };

  const handleRequestNewLink = async () => {
    try {
      if (token) {
        await forgotPassword({ token });
        await navigate("/email-sent");
      } else {
        await navigate("/forgot-password");
      }
    } catch {
      await navigate("/forgot-password");
    }
  };

  if (isLoading) {
    return <OverlaySpinner />;
  }

  if (error) {
    return (
      <AuthLayout
        title={t(($) => $.password.reset.invalidLink.title)}
        description={t(($) => $.password.reset.invalidLink.description)}
      >
        <NotFound />
      </AuthLayout>
    );
  }

  if (!data) {
    return (
      <AuthLayout
        title={t(($) => $.password.reset.invalidLink.title)}
        description={t(($) => $.password.reset.invalidLink.description)}
      >
        <div className="pt-4">
          <Button onClick={handleRequestNewLink}>
            {t(($) => $.password.reset.invalidLink.requestNewLink)}
          </Button>
        </div>
      </AuthLayout>
    );
  }

  if (data.isTokenValid === false) {
    // Redirect to login page if token is expired or invalid. We do this
    // so that users who click on an expired link from their email don't
    // think they have to request a new link.
    return <Navigate to="/login" replace />;
  }

  if (data.isSsoAvailable && token) {
    return (
      <AuthLayout description={t(($) => $.password.sso.create.description)}>
        <div className="mt-5 flex items-center justify-between">
          <Button onClick={() => handleSignInWithSso(token)}>
            {t(($) => $.commons.buttons.proceed)}
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t(($) => $.password.reset.setNew)}>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="mt-4 grid gap-4"
        >
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t(($) => $.password.title)}</FormLabel>
                <FormDescription>
                  {t(($) => $.settings.password.mustContain)}
                </FormDescription>
                <FormControl>
                  <Input
                    type="password"
                    placeholder={t(($) => $.password.new)}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="repeatPass"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t(($) => $.password.repeat)}</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder={t(($) => $.password.repeat)}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end">
            <Button type="submit" disabled={!form.formState.isValid}>
              {t(($) => $.commons.buttons.confirm)}
            </Button>
          </div>
        </form>
      </Form>
    </AuthLayout>
  );
};
